import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { DisplayTable } from '@/components/tables/DisplayTable/DisplayTable';
import { TableRowSkeleton } from '@/components/tables/TableRowSkeleton/TableRowSkeleton';
import { getGrantorOptionsFromData } from '@/modules/entities/inputs/PrimaryClientDropdown/PrimaryClientDropdown.utils';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useLifeInsuranceInference } from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/hooks/useLifeInsuranceInference';
import { useLifeInsuranceStepData } from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/hooks/useLifeInsuranceStepData';
import { useOwnerEntityOptions } from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/hooks/useOwnerEntityOptions';
import { LifeInsuranceStepFormShape } from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/LifeInsuranceStep.types';
import { LifeInsuranceStepEditDrawer } from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/LifeInsuranceStepEditDrawer';
import { LifeInsuranceStepRow } from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/LifeInsuranceStepRow';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';

import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { LifeInsuranceImportForm } from './hooks/useLifeInsuranceImportForm';

const COLUMNS = [
  { headerName: '', width: 40 },
  { headerName: 'Name', flex: 1 },
  { headerName: 'Policy type', width: 168 },
  { headerName: 'Owner', flex: 1 },
  { headerName: 'Insured individuals', flex: 1 },
  {
    headerName: 'Policy death benefit',
    flex: 1,
    align: 'right' as const,
  },
  { headerName: 'Other linked fields', width: 130 },
  { headerName: '', width: 48 },
];

export interface LifeInsuranceImportPaneProps {
  form: LifeInsuranceImportForm;
}

/** "Import data" → Life insurance (eMoney only). */
export function LifeInsuranceImportPane({
  form,
}: LifeInsuranceImportPaneProps) {
  const { householdId } = useManageIntegrationDataContext();
  const { linkedHouseholdAssetProvider, possibleGrantors } =
    useHouseholdDetailsContext();
  const { formMethods, seed, setOwnerEntityData } = form;

  const { defaultValues, integrationInsurances, loading } =
    useLifeInsuranceStepData({
      householdId,
      linkedHouseholdAssetProvider,
      // The pane only mounts once it's open, so this refires each time the user
      // returns — picking up a changed client selection, and dropping policies
      // that have since been imported.
      fetchPolicy: 'cache-and-network',
    });

  const {
    options: ownerEntityOptions,
    entityKindById,
    entityStageById,
    subtypeIdByEntityId,
  } = useOwnerEntityOptions();
  const insuredIndividualOptions = useMemo(
    () => getGrantorOptionsFromData(possibleGrantors ?? []),
    [possibleGrantors]
  );
  const editablePolicyTypeIds = useMemo(
    () =>
      new Set(
        Object.entries(defaultValues.policies)
          .filter(([, policy]) => !policy.policyType)
          .map(([policyId]) => policyId)
      ),
    [defaultValues.policies]
  );
  const inferringPolicyIds = useLifeInsuranceInference({
    formMethods,
    integrationInsurances,
    loading,
    policyHolderOptions: insuredIndividualOptions,
  });

  useEffect(() => {
    setOwnerEntityData(entityKindById, entityStageById, subtypeIdByEntityId);
  }, [
    entityKindById,
    entityStageById,
    setOwnerEntityData,
    subtypeIdByEntityId,
  ]);

  useEffect(() => {
    if (loading) return;
    seed(defaultValues);
  }, [defaultValues, loading, seed]);

  return (
    <FormProvider<LifeInsuranceStepFormShape> {...formMethods}>
      <LifeInsuranceImportPaneInner
        loading={loading}
        ownerEntityOptions={ownerEntityOptions}
        insuredIndividualOptions={insuredIndividualOptions}
        editablePolicyTypeIds={editablePolicyTypeIds}
        inferringPolicyIds={inferringPolicyIds}
      />
    </FormProvider>
  );
}

interface LifeInsuranceImportPaneInnerProps {
  loading: boolean;
  ownerEntityOptions: ReturnType<typeof useOwnerEntityOptions>['options'];
  insuredIndividualOptions: ReturnType<typeof getGrantorOptionsFromData>;
  editablePolicyTypeIds: Set<string>;
  inferringPolicyIds: Record<string, boolean>;
}

function LifeInsuranceImportPaneInner({
  loading,
  ownerEntityOptions,
  insuredIndividualOptions,
  editablePolicyTypeIds,
  inferringPolicyIds,
}: LifeInsuranceImportPaneInnerProps) {
  const { isSaving } = useManageIntegrationDataContext();
  const { control } = useFormContext<LifeInsuranceStepFormShape>();

  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);

  const policies = useWatch<LifeInsuranceStepFormShape>({
    control,
    name: 'policies',
  }) as LifeInsuranceStepFormShape['policies'];

  const policyEntries = useMemo(
    () => Object.entries(policies ?? {}),
    [policies]
  );

  if (!loading && policyEntries.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body-s" color={COLORS.TEXT.SECONDARY}>
          No unlinked insurance policies found for this household.
        </Typography>
      </Box>
    );
  }

  return (
    <FormFieldsDisabledProvider isDisabled={isSaving}>
      <DisplayTable columns={COLUMNS}>
        {loading
          ? Array.from({ length: 4 }, (_, index) => (
              <TableRowSkeleton key={index} columns={COLUMNS.length} />
            ))
          : policyEntries.map(([id, row]) => (
              <LifeInsuranceStepRow
                key={id}
                policyId={id}
                name={row.name}
                deathBenefitAmount={
                  row.deathBenefitAmount
                    ? formatCurrencyNoDecimalsAccounting(row.deathBenefitAmount)
                    : null
                }
                ownerEntityOptions={ownerEntityOptions}
                insuredIndividualOptions={insuredIndividualOptions}
                isPolicyTypeEditable={editablePolicyTypeIds.has(id)}
                isInferring={!!inferringPolicyIds[id]}
                onEdit={setEditingPolicyId}
                importTableLayout
                disableUntilSelected
              />
            ))}
      </DisplayTable>

      {editingPolicyId && (
        <LifeInsuranceStepEditDrawer
          key={editingPolicyId}
          policyId={editingPolicyId}
          onClose={() => setEditingPolicyId(null)}
        />
      )}
    </FormFieldsDisabledProvider>
  );
}
